import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Lock, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Modal } from '../ui/Modal';
import {
  METRIC_DEFINITIONS,
  COMBINED_METRICS,
} from '../../constants/metricDefinitions';
import { resolveTiers } from '../../config/metricGroups';
import { StockCategoryFilter } from '../../types/stockCategories';
import { StepperTracker } from '../ui/StepperTracker';
import { useToastNotification } from '../../contexts/ToastContext';

interface MobileMetricCardsProps {
  // All metric values as a flat map
  metricsMap: Record<string, string | null>;
  // Dynamic group selection from backend
  selectedGroups?: [string, string] | null;
  // Component props
  loading?: boolean;
  className?: string;
  visibleHints?: number;
  category?: StockCategoryFilter;
  spotlightStep?: string | null;
  isStreamerMode?: boolean;
}

const TOTAL_TIERS = 4;

const SPOTLIGHT_STEP_MAPPING: Record<string, number> = {
  group1Analysis: 0,
  group2Analysis: 1,
  group3Analysis: 2,
  group4Analysis: 3,
};

/**
 * Determine which tier index is unlocked based on visibleHints.
 * tier0 always unlocked, tier1 at visibleHints >= 2, tier2 >= 3, tier3 >= 4
 */
function getUnlockedTierCount(visibleHints: number): number {
  if (visibleHints >= 4) return 4;
  if (visibleHints >= 3) return 3;
  if (visibleHints >= 2) return 2;
  return 1; // tier0 always unlocked
}

export const MobileMetricCards: React.FC<MobileMetricCardsProps> = ({
  metricsMap,
  selectedGroups,
  loading = false,
  className,
  visibleHints = 0,
  spotlightStep,
  isStreamerMode = false,
}) => {
  const { showToast } = useToastNotification();
  const unlockedCount = getUnlockedTierCount(visibleHints);

  // Resolve dynamic tiers based on selectedGroups
  const { tierMetrics, tierLabels } = React.useMemo(
    () => resolveTiers(selectedGroups),
    [selectedGroups]
  );

  const TIER_KEYS_DYNAMIC = ['tier0', 'tier1', 'tier2', 'tier3'];

  const dynamicStepperSteps = React.useMemo(
    () => tierLabels.map(label => ({ label })),
    [tierLabels]
  );

  // Phase-based animation state machine for tier transitions
  type AnimationPhase = 'idle' | 'title-exit' | 'title-enter' | 'metric-flip';
  const [animPhase, setAnimPhase] = useState<AnimationPhase>('idle');
  const [animatingTierIndex, setAnimatingTierIndex] = useState<number | null>(null);
  const [displayedTierIndex, setDisplayedTierIndex] = useState(0);

  // Interactive stepper state (mirroring HintsSection pattern)
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const prevUnlockedCountRef = useRef(unlockedCount);

  // Track whether the analysis dialogue is driving the stepper
  const [dialogueTierOverride, setDialogueTierOverride] = useState<number | null>(null);

  // Listen for dialogue step changes directly (bypasses prop chain / React.memo)
  // This mimics clicking the stepper — it calls setActiveStepIndex locally
  useEffect(() => {
    const handler = (e: Event) => {
      const step = (e as CustomEvent).detail as string | null;
      if (step && step in SPOTLIGHT_STEP_MAPPING) {
        const tierIndex = SPOTLIGHT_STEP_MAPPING[step];
        setDialogueTierOverride(tierIndex);
        setActiveStepIndex(tierIndex);
      } else {
        setDialogueTierOverride(null);
      }
    };
    window.addEventListener('baron-spotlight-step', handler);
    return () => window.removeEventListener('baron-spotlight-step', handler);
  }, []);

  // Derive effective values — dialogue override takes priority
  const spotlightTierIndex = spotlightStep && spotlightStep in SPOTLIGHT_STEP_MAPPING
    ? SPOTLIGHT_STEP_MAPPING[spotlightStep]
    : undefined;
  const effectiveStepIndex = dialogueTierOverride ?? spotlightTierIndex ?? activeStepIndex;

  // During analysis dialogue, treat all tiers as unlocked so the stepper
  // shows the active indicator and actual metrics are visible
  const effectiveUnlockedCount = (dialogueTierOverride !== null || spotlightTierIndex !== undefined)
    ? TOTAL_TIERS
    : unlockedCount;

  // Timeout refs for phased animation
  const animTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearAnimTimers = useCallback(() => {
    animTimersRef.current.forEach(clearTimeout);
    animTimersRef.current = [];
  }, []);

  // Auto-advance to the newest unlocked tier when unlockedCount increases
  useEffect(() => {
    const prev = prevUnlockedCountRef.current;
    if (unlockedCount > prev && prev > 0) {
      const newTierIndex = unlockedCount - 1;
      setActiveStepIndex(newTierIndex);

      // Only animate for single-tier unlocks (wrong guess)
      // Correct guess jumps by 2+ tiers — skip animation
      if (unlockedCount === prev + 1) {
        clearAnimTimers();
        setAnimatingTierIndex(newTierIndex);

        // Phase 1: title-exit (old tier fades out left)
        setAnimPhase('title-exit');
        // displayedTierIndex still shows the OLD tier during exit

        const t1 = setTimeout(() => {
          // Phase 2: title-enter (swap to new tier, fade in from right)
          setDisplayedTierIndex(newTierIndex);
          setAnimPhase('title-enter');
        }, 250);

        const t2 = setTimeout(() => {
          // Phase 3: metric-flip (staggered vertical flip per cell)
          setAnimPhase('metric-flip');
        }, 550);

        const t3 = setTimeout(() => {
          // Phase 4: idle — clear all animation state
          setAnimPhase('idle');
          setAnimatingTierIndex(null);
        }, 1310);

        animTimersRef.current = [t1, t2, t3];
      } else {
        // Multi-tier jump (correct guess): instant update, no animation
        setDisplayedTierIndex(newTierIndex);
        setAnimPhase('idle');
        setAnimatingTierIndex(null);
      }

      prevUnlockedCountRef.current = unlockedCount;
      return () => clearAnimTimers();
    }
    if (unlockedCount !== prev) {
      prevUnlockedCountRef.current = unlockedCount;
      setDisplayedTierIndex(Math.max(0, unlockedCount - 1));
    }
  }, [unlockedCount, clearAnimTimers]);

  const handleStepClick = useCallback(
    (index: number) => {
      if (index < unlockedCount && animPhase === 'idle') {
        setActiveStepIndex(index);
        setDisplayedTierIndex(index);
      }
    },
    [unlockedCount, animPhase]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      let nextIndex: number | null = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        for (let i = index + 1; i < TOTAL_TIERS; i++) {
          if (i < unlockedCount) { nextIndex = i; break; }
        }
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        for (let i = index - 1; i >= 0; i--) {
          if (i < unlockedCount) { nextIndex = i; break; }
        }
      } else if (e.key === 'Home') {
        e.preventDefault();
        nextIndex = 0;
      } else if (e.key === 'End') {
        e.preventDefault();
        nextIndex = Math.max(0, unlockedCount - 1);
      }
      if (nextIndex !== null && nextIndex < unlockedCount && animPhase === 'idle') {
        setActiveStepIndex(nextIndex);
        setDisplayedTierIndex(nextIndex);
        const btn = document.getElementById(`financial-step-${nextIndex}`);
        btn?.focus();
      }
    },
    [unlockedCount, animPhase]
  );

  // Metric values mapped to keys — use the flat map passed in, with legacy alias
  const metricValues: Record<string, string | null> = React.useMemo(() => {
    const map = { ...metricsMap };
    // Alias marketCap → marketCapitalization for METRIC_DEFINITIONS key
    if (map.marketCap !== undefined && map.marketCapitalization === undefined) {
      map.marketCapitalization = map.marketCap;
    }
    // Suppress $0 netIncome
    if (map.netIncome === '$0') {
      map.netIncome = null;
    }
    // Alias earningsPerShare → dilutedEps
    if (map.dilutedEps === undefined && map.earningsPerShare !== undefined) {
      map.dilutedEps = map.earningsPerShare;
    }
    return map;
  }, [metricsMap]);

  // Definition modal state
  const [definitionModal, setDefinitionModal] = React.useState<{
    key: string;
    isOpen: boolean;
  }>({ key: '', isOpen: false });

  const openDefinition = (key: string) => {
    setDefinitionModal({ key, isOpen: true });
  };

  const closeDefinition = () => {
    setDefinitionModal(prev => ({ ...prev, isOpen: false }));
  };

  const activeDefinition = definitionModal.key ? METRIC_DEFINITIONS[definitionModal.key] : null;

  // Get display value for a metric
  const getDisplayValue = (key: string): string => {
    if (loading) return '...';
    // Handle combined metrics
    if (key in COMBINED_METRICS) {
      const mapping = COMBINED_METRICS[key];
      const primary = metricValues[mapping.primary];
      const secondary = metricValues[mapping.secondary];
      const pVal = primary || 'N/A';
      const sVal = secondary || 'N/A';
      return `${pVal} (${sVal})`;
    }
    return metricValues[key] || 'N/A';
  };

  // Get the currently focused metric value for modal
  const getModalValue = (): string => {
    if (!definitionModal.key) return 'N/A';
    return getDisplayValue(definitionModal.key);
  };

  // Check if a tier's metric is locked
  const isTierLocked = (tierIndex: number) => tierIndex >= effectiveUnlockedCount;

  return (
    <div className={cn('flex flex-col animate-fade-up', className)}>
      {/* Header row */}
      {!isStreamerMode && (
        <div className="flex items-center justify-between px-1 mb-4">
          <h3 className="font-serif text-[clamp(1.25rem,2.5vw,1.625rem)] font-normal" style={{ color: 'var(--clue-text-primary)' }}>
            Financial Clues
          </h3>
          <span
            className="font-mono text-[clamp(0.625rem,1.2vw,0.75rem)] font-medium px-3 py-1 rounded-full"
            style={{ color: 'var(--clue-text-tertiary)', backgroundColor: 'var(--clue-bg-secondary)' }}
          >
            {unlockedCount} of {TOTAL_TIERS}
          </span>
        </div>
      )}

      {/* Stepper Track */}
      {!isStreamerMode && (
        <StepperTracker
          steps={dynamicStepperSteps}
          activeStepIndex={effectiveStepIndex}
          visibleCount={effectiveUnlockedCount}
          onStepClick={handleStepClick}
          onLockedStepClick={() => showToast('Make an incorrect guess and a new hint will be revealed!')}
          onStepKeyDown={handleKeyDown}
          stepIdPrefix="financial-step"
          ariaLabel="Financial clues progress"
          interactive={true}
        />
      )}

      {/* Announcement region */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {effectiveStepIndex < effectiveUnlockedCount
          ? `Viewing ${tierLabels[effectiveStepIndex] || TIER_KEYS_DYNAMIC[effectiveStepIndex]} clues, tier ${effectiveStepIndex + 1} of ${TOTAL_TIERS}`
          : `Tier ${effectiveStepIndex + 1} is locked. Make a guess to unlock.`}
      </div>

      {/* Single active tier content */}
      {(() => {
        // During animation, show the displayedTierIndex content (which transitions old→new)
        const contentIndex = animPhase !== 'idle' ? displayedTierIndex : effectiveStepIndex;
        const isAnimTarget = animatingTierIndex !== null && animPhase !== 'idle';

        // Determine title animation class
        let titleAnimClass: string | undefined;
        if (isAnimTarget) {
          if (animPhase === 'title-exit') titleAnimClass = 'animate-title-exit';
          else if (animPhase === 'title-enter') titleAnimClass = 'animate-title-enter';
        }

        // Determine metric animation state
        const metricFlipActive = isAnimTarget && animPhase === 'metric-flip';
        const metricsHidden = isAnimTarget && (animPhase === 'title-exit' || animPhase === 'title-enter');

        const tierContent = (
          <div role="tabpanel" aria-labelledby={`financial-step-${effectiveStepIndex}`} className={isStreamerMode ? 'flex-1 min-w-0' : undefined}>
            {isTierLocked(contentIndex) ? (
              <LockedTierCard
                tierIndex={contentIndex}
                tierLabel={tierLabels[contentIndex] || TIER_KEYS_DYNAMIC[contentIndex]}
              />
            ) : (
              <UnlockedTierRow
                tierKey={TIER_KEYS_DYNAMIC[contentIndex]}
                tierIndex={contentIndex}
                tierLabel={tierLabels[contentIndex] || TIER_KEYS_DYNAMIC[contentIndex]}
                metrics={tierMetrics[contentIndex]}
                metricValues={metricValues}
                loading={loading}
                isAnimating={false}
                onMetricClick={openDefinition}
                titleAnimClass={titleAnimClass}
                metricFlipActive={metricFlipActive}
                metricsHidden={metricsHidden}
              />
            )}
          </div>
        );

        if (!isStreamerMode) return tierContent;

        return (
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Previous tier"
              disabled={effectiveStepIndex <= 0}
              onClick={() => handleStepClick(effectiveStepIndex - 1)}
              className="p-0.5 rounded transition-colors disabled:opacity-30 flex-shrink-0"
              style={{ color: 'var(--clue-text-secondary)' }}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            {tierContent}
            <button
              type="button"
              aria-label="Next tier"
              disabled={effectiveStepIndex >= effectiveUnlockedCount - 1}
              onClick={() => handleStepClick(effectiveStepIndex + 1)}
              className="p-0.5 rounded transition-colors disabled:opacity-30 flex-shrink-0"
              style={{ color: 'var(--clue-text-secondary)' }}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        );
      })()}

      {/* Single shared definition modal */}
      {activeDefinition && (
        <Modal
          isOpen={definitionModal.isOpen}
          onClose={closeDefinition}
          title={activeDefinition.label.toUpperCase()}
          titleCentered={true}
          headerClassName="px-4 sm:px-6 pt-4 pb-1"
        >
          <div className="p-4 pt-0 space-y-3">
            <p className="text-sm leading-relaxed text-black dark:text-white">
              {activeDefinition.explanation}
            </p>
            <div className="flex flex-col items-center gap-1 py-2">
              <span className="text-sm font-medium text-muted-foreground">Current value</span>
              <span className="font-mono text-lg font-bold px-4 py-1.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                {getModalValue()}
              </span>
            </div>
            <div className="rounded-md border border-border bg-background/80 dark:bg-card/80 px-3 py-2">
              <p className="text-xs font-mono text-foreground break-words">
                {activeDefinition.formula}
              </p>
            </div>
            {activeDefinition.example && (
              <p className="text-xs italic text-muted-foreground">
                {activeDefinition.example}
              </p>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

// ─── Unlocked Tier Row ───────────────────────────────────────────────

interface UnlockedTierRowProps {
  tierKey: string;
  tierIndex: number;
  tierLabel: string;
  metrics: string[];
  metricValues: Record<string, string | null>;
  loading: boolean;
  isAnimating: boolean;
  onMetricClick: (key: string) => void;
  titleAnimClass?: string;
  metricFlipActive?: boolean;
  metricsHidden?: boolean;
}

const UnlockedTierRow: React.FC<UnlockedTierRowProps> = ({
  tierKey,
  tierIndex,
  tierLabel,
  metrics,
  metricValues,
  loading,
  isAnimating,
  onMetricClick,
  titleAnimClass,
  metricFlipActive,
  metricsHidden,
}) => {
  return (
    <div
      data-spotlight-id={`metric-group-${tierIndex + 1}`}
      className={cn(
        'rounded-2xl px-5 py-3 transition-shadow duration-200',
        isAnimating && 'animate-hint-reveal'
      )}
      style={{
        backgroundColor: 'var(--clue-bg-card)',
        border: '1px solid var(--clue-border-light)',
        boxShadow: 'var(--clue-shadow-sm)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--clue-shadow-md)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'var(--clue-shadow-sm)'; }}
    >
      {/* Tier header */}
      <div className="flex items-center gap-2 mb-2 overflow-hidden">
        <div
          className="w-[7px] h-[7px] rounded-full flex-shrink-0"
          style={{ backgroundColor: 'var(--clue-green-accent)' }}
        />
        <span
          className={cn(
            'font-sans text-[clamp(0.6rem,1.2vw,0.6875rem)] font-semibold uppercase',
            titleAnimClass
          )}
          style={{ letterSpacing: '1.4px', color: 'var(--clue-text-secondary)' }}
        >
          {tierLabel}
        </span>
      </div>

      {/* Metrics in horizontal grid with hairline dividers */}
      <div
        className="grid grid-cols-3 gap-px rounded-lg overflow-hidden"
        style={{
          backgroundColor: 'var(--clue-border-light)',
          perspective: metricFlipActive ? '800px' : undefined,
        }}
      >
        {metrics.map((key, i) => {
          const def = METRIC_DEFINITIONS[key];
          if (!def) return null;

          const isCombined = key in COMBINED_METRICS;
          let displayValue: string;

          if (loading) {
            displayValue = '...';
          } else if (isCombined) {
            const mapping = COMBINED_METRICS[key];
            const primary = metricValues[mapping.primary];
            const secondary = metricValues[mapping.secondary];
            displayValue = `${primary || 'N/A'} (${secondary || 'N/A'})`;
          } else {
            displayValue = metricValues[key] || 'N/A';
          }

          return (
            <button
              key={key}
              type="button"
              onClick={() => onMetricClick(key)}
              className={cn(
                'flex flex-col items-center justify-center px-2 py-1.5 min-w-0',
                'focus:outline-none rounded',
                'transition-colors duration-150',
                'active:scale-[0.98]',
                metricFlipActive && 'animate-metric-flip-vertical',
              )}
              style={{
                backgroundColor: 'var(--clue-bg-card)',
                ...(metricsHidden ? { opacity: 0 } : {}),
                ...(metricFlipActive ? { animationDelay: `${i * 180}ms` } : {}),
              }}
            >
              <span
                className="font-mono text-[clamp(1rem,2vw,1.25rem)] font-semibold leading-tight truncate w-full text-center"
                style={{ color: 'var(--clue-text-primary)', letterSpacing: '-0.3px' }}
              >
                {displayValue}
              </span>
              <span
                className="font-sans text-[clamp(0.5rem,1vw,0.625rem)] font-medium uppercase mt-1 truncate w-full text-center"
                style={{ letterSpacing: '1.2px', color: 'var(--clue-text-tertiary)' }}
              >
                {def.mobileLabel}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─── Locked Tier Card (full-width, matching Company Hints style) ─────

interface LockedTierCardProps {
  tierIndex: number;
  tierLabel: string;
}

const LockedTierCard: React.FC<LockedTierCardProps> = ({
  tierIndex,
  tierLabel,
}) => {
  return (
    <div
      data-spotlight-id={`metric-group-${tierIndex + 1}`}
      className="rounded-2xl flex flex-col items-center justify-center py-10 px-6 text-center"
      style={{
        border: '2px dashed var(--clue-border-mid)',
        backgroundColor: 'var(--clue-bg-locked)',
      }}
    >
      <div
        className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
        style={{ backgroundColor: 'var(--clue-bg-secondary)' }}
      >
        <Lock className="w-5 h-5" style={{ color: 'var(--clue-text-locked)' }} />
      </div>
      <p className="font-serif text-[clamp(0.9375rem,1.8vw,1.125rem)] font-normal mb-1" style={{ color: 'var(--clue-text-primary)' }}>
        {tierLabel}
      </p>
      <p className="text-[clamp(0.75rem,1.4vw,0.875rem)]" style={{ color: 'var(--clue-text-secondary)' }}>
        Make a guess to unlock the next clue
      </p>
    </div>
  );
};

export default MobileMetricCards;
